import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice, STAGE_PRICES } from '../utils/currency';
import { Link } from 'react-router-dom';

const Stage1Modal = ({ isOpen, onClose, productName, onOpenContactForm }) => {
  const { t, language } = useLanguage();

  const steps = [
    {
      number: '1',
      title: t('stage1Modal.step1Title') || 'Remove ECU',
      description: t('stage1Modal.step1Desc') || 'Carefully remove the ECU from your motorcycle following your bike\'s service manual.',
      icon: '🔧'
    },
    {
      number: '2',
      title: t('stage1Modal.step2Title') || 'Package & Send',
      description: t('stage1Modal.step2Desc') || 'Securely package your ECU and send it to us. We\'ll provide you with detailed shipping instructions.',
      icon: '📦'
    },
    {
      number: '3',
      title: t('stage1Modal.step3Title') || 'Professional Tuning',
      description: t('stage1Modal.step3Desc') || 'Our experts will modify your ECU. The process takes 2-4 hours depending on the model.',
      icon: '⚙️'
    },
    {
      number: '4',
      title: t('stage1Modal.step4Title') || 'Receive & Install',
      description: t('stage1Modal.step4Desc') || 'We\'ll send your tuned ECU back to you. Simply reinstall it on your motorcycle.',
      icon: '✅'
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-600 to-red-700 p-6 flex items-center justify-between border-b border-red-900 flex-shrink-0">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    {t('stage1Modal.title') || 'Stage 1 ECU Tuning Service'}
                  </h2>
                  <p className="text-red-100 mt-1">
                    {formatPrice(STAGE_PRICES.stage1, language)}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-red-800 rounded-lg p-2 transition-all"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 overflow-y-auto flex-1">
                {/* Description */}
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  {t('stage1Modal.description') || 'Transform your motorcycle\'s performance with our professional Stage 1 ECU tuning service. Here\'s how the process works:'}
                </p>

                {/* Steps */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {steps.map((step, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-racing-red transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-racing-red text-white font-bold text-lg">
                            {step.number}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-bold text-white mb-2">{step.title}</h3>
                          <p className="text-gray-400 text-sm">{step.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Payment & Dyno Info */}
                <div className="space-y-4 mb-8">
                  <div className="bg-blue-900/30 border border-blue-700 rounded-xl p-6">
                    <h3 className="font-bold text-blue-300 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                      </svg>
                      {t('stage1Modal.paymentTitle') || 'Payment'}
                    </h3>
                    <p className="text-gray-300">
                      {t('stage1Modal.paymentDesc') || 'We\'ll send you a secure payment link during the modification process for your convenience and security.'}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-center gap-3">
                  <button
                    onClick={onOpenContactForm}
                    className="px-6 py-3 bg-racing-red hover:bg-red-700 text-white font-bold rounded-lg transition-all"
                  >
                    {t('stage1Modal.contactButton') || 'Contact Us to Order'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Stage1Modal;
